import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  createOffer,
  updateOffer,
  getOffersList,
} from "../../appRedux/actions/offers";
import { getPlatformsList } from "../../appRedux/actions/platforms";
import { hideDialog as hideDialogCommon } from "../../appRedux/actions/common";
import config from "../../utils/config";

const LEVELS = [
  { value: "GLOBAL", label: "Global" },
  { value: "MERCHANT", label: "Merchant" },
  { value: "COURSE", label: "Course" },
];
const STATUSES = [
  { value: "DRAFT", label: "Draft" },
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
];
const DISCOUNT_TYPES = [
  { value: "PERCENT", label: "Percentage" },
  { value: "FLAT", label: "Flat amount" },
];

const INITIAL = {
  title: "",
  description: "",
  level: "GLOBAL",
  merchant: "",
  courseId: "",
  discountType: "PERCENT",
  discountValue: "",
  maxDiscountAmount: "",
  priority: 100,
  status: "DRAFT",
  startAt: "",
  endAt: "",
};

function FormView({ isUpdate }) {
  const dispatch = useDispatch();
  const offerInfo = useSelector((state) => state.offers.offerInfo);
  const platformList = useSelector((state) => state.platforms.platformList || []);
  const [initialValues, setInitialValues] = useState(INITIAL);

  useEffect(() => {
    dispatch(getPlatformsList({ page: 1, size: 100 }));
  }, [dispatch]);

  useEffect(() => {
    if (isUpdate && offerInfo && offerInfo._id) {
      setInitialValues({
        title: offerInfo.title || "",
        description: offerInfo.description || "",
        level: offerInfo.level || "GLOBAL",
        merchant: offerInfo.merchant || "",
        courseId: offerInfo.courseId?._id || offerInfo.courseId || "",
        discountType: offerInfo.discountType || "PERCENT",
        discountValue: offerInfo.discountValue ?? "",
        maxDiscountAmount: offerInfo.maxDiscountAmount ?? "",
        priority: offerInfo.priority ?? 100,
        status: offerInfo.status || "DRAFT",
        startAt: offerInfo.startAt
          ? new Date(offerInfo.startAt).toISOString().slice(0, 16)
          : "",
        endAt: offerInfo.endAt
          ? new Date(offerInfo.endAt).toISOString().slice(0, 16)
          : "",
      });
    } else {
      setInitialValues(INITIAL);
    }
  }, [isUpdate, offerInfo]);

  const schema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    level: Yup.string().oneOf(["GLOBAL", "MERCHANT", "COURSE"]).required(),
    merchant: Yup.string().when("level", {
      is: "MERCHANT",
      then: (s) => s.required("Merchant is required for Merchant level"),
    }),
    courseId: Yup.string().when("level", {
      is: "COURSE",
      then: (s) => s.required("Course is required for Course level"),
    }),
    discountType: Yup.string().oneOf(["PERCENT", "FLAT"]).required(),
    discountValue: Yup.number()
      .min(0, "Must be ≥ 0")
      .when("discountType", {
        is: "PERCENT",
        then: (n) => n.max(100, "Percentage must be ≤ 100"),
      })
      .required("Discount value is required"),
    priority: Yup.number().min(0).optional(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: schema,
    onSubmit: (values) => {
      const payload = {
        title: values.title,
        description: values.description,
        level: values.level,
        merchant: values.level === "MERCHANT" ? values.merchant : undefined,
        courseId: values.level === "COURSE" ? values.courseId : undefined,
        discountType: values.discountType,
        discountValue: Number(values.discountValue),
        maxDiscountAmount:
          values.maxDiscountAmount !== ""
            ? Number(values.maxDiscountAmount)
            : null,
        priority: Number(values.priority) || 100,
        status: values.status,
        startAt: values.startAt || null,
        endAt: values.endAt || null,
      };

      if (isUpdate && offerInfo?._id) {
        dispatch(updateOffer(offerInfo._id, payload))
          .then(() => {
            dispatch(getOffersList()).catch(() => {});
            dispatch(hideDialogCommon());
          })
          .catch(() => {});
      } else {
        dispatch(createOffer(payload))
          .then(() => {
            dispatch(getOffersList()).catch(() => {});
            dispatch(hideDialogCommon());
          })
          .catch(() => {});
      }
    },
  });

  const {
    errors,
    touched,
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    getFieldProps,
  } = formik;

  const merchants = platformList || [];

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={handleSubmit} className="signin-form">
        <div className="row m-0">
          <div className="col-md-12">
            <div className="input-block mb-3">
              <label className="col-form-label">Title <span className="text-danger">*</span></label>
              <input
                className="form-control"
                type="text"
                {...getFieldProps("title")}
              />
              {touched.title && errors.title && (
                <p className="error-msg text-danger">{errors.title}</p>
              )}
            </div>
          </div>
          <div className="col-md-12">
            <div className="input-block mb-3">
              <label className="col-form-label">Description</label>
              <textarea
                className="form-control"
                rows={2}
                {...getFieldProps("description")}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="input-block mb-3">
              <label className="col-form-label">Level <span className="text-danger">*</span></label>
              <select
                className="form-control"
                {...getFieldProps("level")}
                onChange={(e) => {
                  setFieldValue("level", e.target.value);
                  setFieldValue("merchant", "");
                  setFieldValue("courseId", "");
                }}
              >
                {LEVELS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {values.level === "MERCHANT" && (
            <div className="col-md-6">
              <div className="input-block mb-3">
                <label className="col-form-label">Merchant <span className="text-danger">*</span></label>
                <select className="form-control" {...getFieldProps("merchant")}>
                  <option value="">Select merchant</option>
                  {merchants.map((m) => (
                    <option key={m._id || m.code} value={m.code}>
                      {m.name}
                    </option>
                  ))}
                </select>
                {touched.merchant && errors.merchant && (
                  <p className="error-msg text-danger">{errors.merchant}</p>
                )}
              </div>
            </div>
          )}
          {values.level === "COURSE" && (
            <div className="col-md-6">
              <div className="input-block mb-3">
                <label className="col-form-label">Course ID <span className="text-danger">*</span></label>
                <input
                  className="form-control"
                  type="text"
                  placeholder="MongoDB ObjectId of the course"
                  {...getFieldProps("courseId")}
                />
                {touched.courseId && errors.courseId && (
                  <p className="error-msg text-danger">{errors.courseId}</p>
                )}
              </div>
            </div>
          )}
          <div className="col-md-6">
            <div className="input-block mb-3">
              <label className="col-form-label">Discount type <span className="text-danger">*</span></label>
              <select className="form-control" {...getFieldProps("discountType")}>
                {DISCOUNT_TYPES.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-6">
            <div className="input-block mb-3">
              <label className="col-form-label">
                Discount value {values.discountType === "PERCENT" ? "(%)" : "(₹)"} <span className="text-danger">*</span>
              </label>
              <input
                className="form-control"
                type="number"
                min={0}
                max={values.discountType === "PERCENT" ? 100 : undefined}
                step={values.discountType === "PERCENT" ? 1 : 0.01}
                {...getFieldProps("discountValue")}
              />
              {touched.discountValue && errors.discountValue && (
                <p className="error-msg text-danger">{errors.discountValue}</p>
              )}
            </div>
          </div>
          <div className="col-md-6">
            <div className="input-block mb-3">
              <label className="col-form-label">Max discount amount (optional)</label>
              <input
                className="form-control"
                type="number"
                min={0}
                {...getFieldProps("maxDiscountAmount")}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="input-block mb-3">
              <label className="col-form-label">Priority</label>
              <input
                className="form-control"
                type="number"
                min={0}
                {...getFieldProps("priority")}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="input-block mb-3">
              <label className="col-form-label">Status</label>
              <select className="form-control" {...getFieldProps("status")}>
                {STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-6">
            <div className="input-block mb-3">
              <label className="col-form-label">Start at (optional)</label>
              <input
                className="form-control"
                type="datetime-local"
                {...getFieldProps("startAt")}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="input-block mb-3">
              <label className="col-form-label">End at (optional)</label>
              <input
                className="form-control"
                type="datetime-local"
                {...getFieldProps("endAt")}
              />
            </div>
          </div>
        </div>
        <div className="submit-section mb-5">
          <button type="submit" className="btn btn-primary submit-btn">
            {isUpdate && offerInfo?._id ? "Update" : "Create"}
          </button>
        </div>
      </Form>
    </FormikProvider>
  );
}

export default FormView;
